import { Button } from '@ambernord/ui';
import { toDataURL } from 'qrcode';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { AppHeader } from '../components/AppHeader.js';
import { mfaEnable, mfaSetup } from '../lib/api.js';
import { RecoveryCodesOnce } from './Security.js';

export default function MfaEnrollment() {
  const { t } = useTranslation('security');
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [setup, setSetup] = useState<{ secret: string; qr: string } | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function begin() {
    setBusy(true); setError(null);
    try {
      const result = await mfaSetup();
      if (!result.ok || !result.data.secret || !result.data.uri) throw Error('setup');
      setSetup({ secret: result.data.secret, qr: await toDataURL(result.data.uri, { width: 208 }) });
    } catch { setError(t('mfa.errors.generic')); }
    finally { setBusy(false); }
  }
  async function confirm(event: FormEvent) {
    event.preventDefault(); setBusy(true); setError(null);
    try {
      const result = await mfaEnable(code);
      if (!result.ok || !result.data.ok) {
        setError(t(result.data.error === 'invalid_code' ? 'mfa.errors.invalidCode' : 'mfa.errors.generic'));
        return;
      }
      // Do not refresh away from the only copy of the recovery codes before acknowledgment.
      setCodes(result.data.recoveryCodes ?? []); setSetup(null); setCode('');
    } catch { setError(t('mfa.errors.generic')); }
    finally { setBusy(false); }
  }
  async function finish() { await refresh(); navigate('/', { replace: true }); }
  return <div className="min-h-full flex flex-col"><AppHeader />
    <main className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-4 py-10">
      <section className="rounded-lg border border-slate-200 p-6 dark:border-slate-800" data-testid="mfa-enrollment-required">
        <h2 className="mb-2 text-base font-medium">{t('mfa.resetHeading')}</h2>
        <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">{t('mfa.resetDescription')}</p>
        {codes ? <RecoveryCodesOnce codes={codes} onDismiss={() => void finish()} /> : setup ?
          <form className="flex flex-col gap-4" onSubmit={(e) => void confirm(e)}>
            <p className="text-sm">{t('mfa.step1')}</p>
            <img src={setup.qr} alt={t('mfa.qrAlt')} width={208} height={208} className="bg-white rounded-lg" />
            <p className="text-xs">{t('mfa.cantScan')}</p><code className="break-all text-sm">{setup.secret}</code>
            <label className="text-sm" htmlFor="enrollment-code">{t('mfa.step2')}</label>
            <input id="enrollment-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className="h-10 rounded-md border border-slate-300 bg-white px-3 text-center text-lg tracking-widest dark:border-slate-700 dark:bg-slate-900" />
            <Button type="submit" disabled={busy || code.length !== 6}>{t('mfa.confirm')}</Button>
          </form> : <Button type="button" disabled={busy} onClick={() => void begin()}>{t('mfa.enable')}</Button>}
        {error && <p className="mt-4 text-sm text-red-600" role="alert">{error}</p>}
      </section>
    </main>
  </div>;
}
